import ConditionalTooltip from './ConditionalTooltip';
import { useTruncateTooltip } from '@/hooks/useTruncateTooltip';
import React, { useEffect, useRef, useState } from 'react';

type TruncateWithTooltipProps = {
  text?: string | null;
  max?: number;
  render: (displayText: string) => React.ReactNode;
  disableTooltip?: boolean;
};

export default function TruncateWithTooltip({
  text,
  max = 20,
  render,
  disableTooltip,
}: TruncateWithTooltipProps) {
  const { displayText, showTooltip, fullText } = useTruncateTooltip(text, max);

  const [tooltipKey, setTooltipKey] = useState(0);
  const hoveringRef = useRef(false);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const onScroll = () => {
      if (!hoveringRef.current) return;
      if (rafRef.current != null) return;

      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        hoveringRef.current = false;
        setTooltipKey((k) => k + 1);
      });
    };

    window.addEventListener('scroll', onScroll, {
      capture: true,
      passive: true,
    });
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return (
    <ConditionalTooltip
      key={tooltipKey}
      show={!disableTooltip && showTooltip}
      title={fullText}
      placement='top'
    >
      <span
        onMouseEnter={() => {
          hoveringRef.current = true;
        }}
        onMouseLeave={() => {
          hoveringRef.current = false;
        }}
        onFocus={() => {
          hoveringRef.current = true;
        }}
        onBlur={() => {
          hoveringRef.current = false;
        }}
      >
        {render(displayText)}
      </span>
    </ConditionalTooltip>
  );
}
